import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '../../contexts/I18nContext';
import { useHoursReport } from '../../hooks/useTaskReports';
import {
  downloadHoursExport,
  type HoursReportTask,
  type ReportFilters,
} from '../../lib/taskReportsApi';
import { FIRMIO_COLORS } from '../dashboard/charts';
import { formatDay, formatHours, ReportCard, SectionState } from './common';
import { CHART_TOOLTIP_STYLE } from '../../lib/chartTheme';

// Estimated vs. actual hours: per-project bar chart, per-person totals and a
// sortable per-task table with an Excel export of the same rows.

type SortKey = 'title' | 'project' | 'assignee' | 'estimate' | 'spent' | 'delta' | 'pct';

function sortValue(task: HoursReportTask, key: SortKey): string | number {
  switch (key) {
    case 'title':
      return task.title.toLowerCase();
    case 'project':
      return task.project?.name.toLowerCase() ?? '';
    case 'assignee':
      return task.assignee?.name.toLowerCase() ?? '';
    case 'estimate':
      return task.estimate ?? -1;
    case 'spent':
      return task.spent;
    case 'delta':
      return task.delta ?? Number.NEGATIVE_INFINITY;
    case 'pct':
      return task.pct ?? Number.NEGATIVE_INFINITY;
  }
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
}: {
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className={`cursor-pointer select-none px-3 py-2 font-medium ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${active ? 'text-gray-900' : 'text-gray-500'}`}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );
}

export function HoursReportSection({ filters }: { filters: ReportFilters }) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useHoursReport(filters);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'spent',
    dir: 'desc',
  });
  const [exporting, setExporting] = useState(false);

  const onSort = (key: SortKey) =>
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );

  const sortedTasks = useMemo(() => {
    const tasks = [...(data?.tasks ?? [])];
    tasks.sort((a, b) => {
      const va = sortValue(a, sort.key);
      const vb = sortValue(b, sort.key);
      const cmp = typeof va === 'number' ? va - (vb as number) : va.localeCompare(vb as string);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return tasks;
  }, [data?.tasks, sort]);

  const chartData = (data?.byProject ?? []).map((p) => ({
    name: p.code ?? p.name,
    estimated: p.estimatedHours,
    spent: p.spentHours,
  }));

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadHoursExport(filters);
    } catch {
      // Surface nothing fancy; the section itself is unaffected.
    } finally {
      setExporting(false);
    }
  };

  return (
    <ReportCard
      title={t('reports.hours.title')}
      actions={
        <button
          type="button"
          onClick={onExport}
          disabled={exporting || !data?.tasks.length}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <Download size={14} />
          {t('reports.hours.export')}
        </button>
      }
    >
      <SectionState isLoading={isLoading} isError={isError} />
      {data && data.tasks.length === 0 && (
        <p className="text-sm text-gray-400">{t('reports.hours.empty')}</p>
      )}
      {data && data.tasks.length > 0 && (
        <div className="space-y-5">
          {chartData.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-gray-500">
                {t('reports.hours.byProject')}
              </p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 12, fill: '#9ca3af' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: '#9ca3af' }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      contentStyle={CHART_TOOLTIP_STYLE}
                      formatter={(value: number) => formatHours(value)}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar
                      dataKey="estimated"
                      name={t('reports.hours.estimated')}
                      fill={FIRMIO_COLORS.blue}
                      radius={[3, 3, 0, 0]}
                    />
                    <Bar
                      dataKey="spent"
                      name={t('reports.hours.spent')}
                      fill={FIRMIO_COLORS.purple}
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {data.byPerson.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-gray-500">
                {t('reports.hours.byPerson')}
              </p>
              <div className="flex flex-wrap gap-2">
                {data.byPerson.map((p) => (
                  <span
                    key={p.userId}
                    className="rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-700"
                  >
                    {p.name ?? t('reports.overruns.unassigned')} ·{' '}
                    <span className="font-semibold">{formatHours(p.spentHours)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="mb-2 text-xs font-medium text-gray-500">{t('reports.hours.byTask')}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs">
                    <SortHeader
                      label={t('reports.hours.task')}
                      sortKey="title"
                      sort={sort}
                      onSort={onSort}
                    />
                    <SortHeader
                      label={t('reports.hours.project')}
                      sortKey="project"
                      sort={sort}
                      onSort={onSort}
                    />
                    <SortHeader
                      label={t('reports.hours.person')}
                      sortKey="assignee"
                      sort={sort}
                      onSort={onSort}
                    />
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      {t('reports.hours.status')}
                    </th>
                    <SortHeader
                      label={t('reports.hours.estimated')}
                      sortKey="estimate"
                      sort={sort}
                      onSort={onSort}
                      align="right"
                    />
                    <SortHeader
                      label={t('reports.hours.spent')}
                      sortKey="spent"
                      sort={sort}
                      onSort={onSort}
                      align="right"
                    />
                    <SortHeader
                      label={t('reports.hours.delta')}
                      sortKey="delta"
                      sort={sort}
                      onSort={onSort}
                      align="right"
                    />
                    <SortHeader
                      label={t('reports.hours.pct')}
                      sortKey="pct"
                      sort={sort}
                      onSort={onSort}
                      align="right"
                    />
                    <th className="px-3 py-2 text-left font-medium text-gray-500">
                      {t('reports.hours.completedAt')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTasks.map((task) => (
                    <tr key={task.id} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-gray-900">
                        {task.identifier ? (
                          <span className="mr-1.5 text-xs text-gray-400">{task.identifier}</span>
                        ) : null}
                        {task.title}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{task.project?.name ?? '—'}</td>
                      <td className="px-3 py-2 text-gray-600">{task.assignee?.name ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span
                          className="inline-flex items-center gap-1.5 text-xs text-gray-600"
                          title={task.status.name}
                        >
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: task.status.color }}
                          />
                          {task.status.name}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {task.estimate != null ? formatHours(task.estimate) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                        {formatHours(task.spent)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums ${
                          task.delta != null && task.delta > 0 ? 'text-red-600' : 'text-gray-600'
                        }`}
                      >
                        {task.delta != null
                          ? `${task.delta > 0 ? '+' : ''}${formatHours(task.delta)}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {task.pct != null ? `${Math.round(task.pct * 100)}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{formatDay(task.completedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </ReportCard>
  );
}
